import { useTranslation } from './useTranslation';
import { useLandingVariant } from './useLandingVariant';
import { LandingVariant } from '../models/landing/variant';

/** Designs that render an online-registration link. */
export const VARIANTS_WITH_ONLINE_LINK: ReadonlySet<LandingVariant> = new Set<LandingVariant>([
  'phone-and-link',
  'link-only',
  'phone-and-link-v1',
  'link-only-v1',
  'phone-and-link-v2',
]);

/**
 * The landing experience the visitor actually sees.
 *
 * - `configuredVariant` — what the `*-landing-variant` payload resolved (base
 *   lp, split arm, or the after-hours swap).
 * - `variant` — the same thing after the no-online-link collapse: a design that
 *   needs an online link falls back to phone-only when the locale has none, so
 *   the page always keeps a working CTA. This is what the visitor saw, and what
 *   is reported as `resolved_lp`.
 *
 * Shared by LandingPage (which renders it) and PostHogPageView (which reports
 * it) so the two can never disagree about which experience was shown.
 */
export const useResolvedLandingVariant = (): {
  variant: LandingVariant;
  configuredVariant: LandingVariant;
} => {
  const { locale, data } = useTranslation();
  const configuredVariant = useLandingVariant(locale ?? '');

  // A new-UX (`-v1`) design collapses to the new-UX phone-only page so the
  // visitor stays inside their A/B arm and `resolved_lp` attribution stays
  // honest; the live designs collapse to the live phone-only page as before.
  const variant: LandingVariant =
    data && !data.ctaOnlineLink && VARIANTS_WITH_ONLINE_LINK.has(configuredVariant)
      ? configuredVariant.endsWith('-v2')
        ? 'phone-only-v2'
        : configuredVariant.endsWith('-v1')
          ? 'phone-only-v1'
          : 'phone-only'
      : configuredVariant;

  return { variant, configuredVariant };
};
